import { roundToPlaces } from "../../utils/roundToPlaces.js";
import { slugify } from "../../utils/slugify.js";
import type { Country } from "../Country.js";
import { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import { Locale } from "../Locale.js";
import type { ProductForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParams } from "../Subscription.js";
import type { SubscriptionDeliveryCount } from "../SubscriptionDeliveryCount.js";
import type { SubscriptionFrequency } from "../SubscriptionFrequency.js";
import { SubscriptionKind } from "../SubscriptionKind.js";
import { SubscriptionSelection } from "../SubscriptionSelection.js";
import type { SubscriptionSize } from "../SubscriptionSize.js";
import { getFathersChoicePrice } from "../getFathersChoicePrice.js";
import { getMothersBlendPrice } from "../getMothersBlendPrice.js";
import { subscriptionVariantNameMap } from "../names/subscriptionVariantNameMap.js";
import { calculatePriceOfSubscription } from "./calculatePriceOfSubscription.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { convertToCurrency } from "./convertToCurrency.js";
import { shouldApplyTax } from "./shouldApplyTax.js";
import { subscriptionDeliveryCountToNumberMap } from "./subscriptionDeliveryCountToNumberMap.js";
import { subscriptionFrequencyToWeeksMap } from "./subscriptionFrequencyToWeeksMap.js";
import { subscriptionSizeToAmountMap } from "./subscriptionSizeToAmountMap.js";
import { subscriptionSizeToWeightMap } from "./subscriptionSizeToWeightMap.js";

const years = 4;
const months = years * 12;
const weeks = years * 52;

/**
 * Calculates the average yearly savings of a subscription
 * compared to buying the equivalent products individually at retail price.
 *
 * Returns a yearly savings amount and percentual savings per deliery.
 */
export function calculateSubscriptionSavings({
	subcategories,
	destinationCountry,
	subscription,
	mothersBlendProduct,
	cafeinaProduct,
	currency,
	vatType,
	exchangeRates,
	mothersBlendConstantPriceEnabled,
	deliveryCount,
}: {
	subcategories: SubcategoryForCustomer[];
	destinationCountry: Country | "outsideEu";
	subscription: SubscriptionParams & { frequency: SubscriptionFrequency; size: SubscriptionSize };
	mothersBlendProduct: ProductForCustomer;
	cafeinaProduct: ProductForCustomer;
	currency: Currency;
	vatType: EconomicSubjectVatType;
	exchangeRates: ExchangeRate[];
	mothersBlendConstantPriceEnabled: boolean;
	deliveryCount: SubscriptionDeliveryCount | undefined;
}): { withTax: number; percentage: number } {
	const subcategory = subcategories.find(({ name }) =>
		Object.values(Locale).every(
			(locale) => slugify(name[locale]) === slugify(subscriptionVariantNameMap[subscription.variant][locale]),
		),
	);
	if (!subcategory) {
		throw new Error(`Subcategory not found: ${subscription.variant}`);
	}

	const taxRate = shouldApplyTax(destinationCountry, vatType)
		? subcategory.vatRates.find(({ country }) => country === destinationCountry)?.rate
		: 0;

	if (taxRate === undefined) {
		throw new Error(`Tax rate not found for country ${destinationCountry}`);
	}

	const amount = subscriptionSizeToAmountMap[subscription.size];
	let retailPricePerDeliveryWithTax: number;

	if (subscription.selection === SubscriptionSelection.FathersChoice) {
		const retailWithoutTaxCzk = getFathersChoicePrice(subscription.size) * amount;
		const retailWithoutTax = convertToCurrency(retailWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
		retailPricePerDeliveryWithTax = calculatePriceWithTax(retailWithoutTax, taxRate);
	} else if (subscription.selection === SubscriptionSelection.MothersBlend && mothersBlendConstantPriceEnabled) {
		const retailWithoutTaxCzk = getMothersBlendPrice(subscription.size) * amount;
		const retailWithoutTax = convertToCurrency(retailWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
		retailPricePerDeliveryWithTax = calculatePriceWithTax(retailWithoutTax, taxRate);
	} else {
		const weight = subscriptionSizeToWeightMap[subscription.size];
		const product = {
			[SubscriptionSelection.MothersBlend]: mothersBlendProduct,
			[SubscriptionSelection.Cafeina]: cafeinaProduct,
		}[subscription.selection];
		const variant = product.variants.find(({ weightInGrams }) => weightInGrams === weight);
		const taxedPrice = variant?.prices[currency][taxRate]?.price;
		if (!taxedPrice) {
			throw new Error("Price not found.");
		}
		retailPricePerDeliveryWithTax = taxedPrice.withTax * amount;
	}

	const discounted = calculatePriceOfSubscription({
		subcategories,
		destinationCountry,
		subscription,
		mothersBlendProduct,
		cafeinaProduct,
		currency,
		vatType,
		exchangeRates,
		mothersBlendConstantPriceEnabled,
		deliveryCount,
	});

	if (deliveryCount !== undefined && subscription.kind === SubscriptionKind.Gift) {
		const subscriptionTotal = discounted.withTax;
		const retailTotal = retailPricePerDeliveryWithTax * subscriptionDeliveryCountToNumberMap[deliveryCount];
		const differenceWithTax = retailTotal - subscriptionTotal;

		return {
			withTax: differenceWithTax,
			percentage: roundToPlaces((retailTotal - subscriptionTotal) / retailTotal, 2),
		};
	}

	const subscriptionTotal = discounted.withTax * months;

	const deliveries = Math.floor(weeks / subscriptionFrequencyToWeeksMap[subscription.frequency]);
	const retailTotal = deliveries * retailPricePerDeliveryWithTax;
	const differenceWithTax = retailTotal - subscriptionTotal;
	const subscriptionPerDelivery = subscriptionTotal / deliveries;

	return {
		withTax: Math.floor(differenceWithTax / years),
		percentage: roundToPlaces(
			(retailPricePerDeliveryWithTax - subscriptionPerDelivery) / retailPricePerDeliveryWithTax,
			2,
		),
	};
}
